import React, { useMemo } from 'react';
import {
  useHistory,
  useLocation,
  useParams as useRouteParams,
  match,
  RouteProps,
  matchPath,
  BrowserRouter as OriginalBrowserRouter,
  BrowserRouterProps,
  Route,
  Redirect,
  Switch,
  useRouteMatch
} from 'react-router-dom';

export type RouterLocationState = unknown;

export interface UseRouter<P> {
  pathname: string;
  search: string;
  params: P;
  state: RouterLocationState;
  push: (path: string, state?: RouterLocationState) => void;
  replace: (path: string, state?: RouterLocationState) => void;
  goBack: () => void;
  matchPath: <Params extends { [K in keyof Params]?: string }>(
    pathname: string,
    props: string | string[] | RouteProps,
    parent?: match<Params> | null
  ) => match<Params> | null;
}

// 這是「預設值」的型別，表示如果不傳泛型，預設是任意字串鍵值對
type DefaultRouteParams = Record<string, string | undefined>;

export const AppRouter: React.FC<BrowserRouterProps> = ({ children, ...props }) => (
  <OriginalBrowserRouter {...props}>{children}</OriginalBrowserRouter>
);

/**
 * 修正版：
 * 1. 移除了 `extends BaseRouteParams` 強制限制，解決 interface 報錯問題。
 * 2. 保留 `= DefaultRouteParams` 作為預設值。
 * 3. 內部使用 `as P` 強制轉型，確保相容性。
 */
export default function useRouter<P = DefaultRouteParams>(): UseRouter<P> {
  const routeHistory = useHistory();
  const routeLocation = useLocation();

  // 這裡加個 as P 讓 TypeScript 閉嘴，相信外部傳入的 interface 結構
  const routeParams = useRouteParams<any>() as P;

  const push = (path: string, state?: RouterLocationState): void => {
    routeHistory.push(path, state);
  };

  const replace = (path: string, state?: RouterLocationState): void => {
    routeHistory.replace(path, state);
  };

  const goBack = (): void => {
    routeHistory.goBack();
  };

  return useMemo(
    () => ({
      pathname: routeLocation.pathname,
      search: routeLocation.search,
      state: routeLocation.state,
      params: routeParams,
      push,
      replace,
      goBack,
      matchPath
    }),
    [routeLocation, routeHistory, routeParams]
  );
}

export { Route, Redirect, Switch, useRouteMatch };
